import React from "react";

import { BSCard, BSBtn, BSBtnDropdown } from "traec-react/utils/bootstrap";
import { confirmDelete } from "traec-react/utils/sweetalert";

import ReportingPeriodRow from "./row";
import useApi, { doFetch } from "storybook-dashboard/utils/fetching";
import { useFullIds } from "storybook-dashboard/utils/hooks";
import { Modal, useModal } from "storybook-dashboard/components/modal";
import { formEventToData } from "AppSrc/utils";
import Moment from "moment";

const repPeriodChoices = ["MONTHS", "DAYS", "WEEKS", "QUARTERS", "YEARS"];

const convertDateToISO = (dateString) => {
  if (!dateString) return null;
  return Moment.utc(dateString).toISOString();
};

const editReportingDuration = (e, { projectId, trigger, hideModal }) => {
  const reporting_data = formEventToData(e);

  const data = {
    reporting: {
      ...reporting_data,
      startDate: convertDateToISO(reporting_data.startDate),
      endDate: convertDateToISO(reporting_data.endDate),
    },
  };

  doFetch(`/api/project/${projectId}/`, "PATCH", data).then((response) => {
    if (response.ok) {
      hideModal();
      console.log("editReportingDuration", trigger);
      trigger?.();
    }
  });
};

const createFromSchedule = (e, { projectId, trigger, hideModal }) => {
  const { schedule } = formEventToData(e);

  doFetch(`/api/project/${projectId}/reporting_periods/`, "POST", { schedule }).then((response) => {
    if (response.ok) {
      hideModal();
      trigger?.();
    }
  });
};

const clearEmpty = (e, { projectId, trigger }) => {
  e.preventDefault();
  confirmDelete({
    text: `This will delete all empty reporting periods after the last period that contains a report.  Are you sure you would like to proceed?`,
    onConfirm: () => {
      doFetch(`/api/project/${projectId}/reporting_periods/`, "POST", {
        clear_empty: true,
      }).then((response) => {
        trigger?.();
      });
    },
  });
};

function RowHeaders() {
  return (
    <div className="row">
      <div className="col-sm-2">
        <b>From</b>
      </div>
      <div className="col-sm-2">
        <b>To</b>
      </div>
      <div className="col-sm-2 text-center">
        <b>Num Reports</b>
      </div>
      <div className="col-sm-5">
        <b>Id</b>
      </div>
      <div className="col-sm-1 text-right">
        <b>Admin</b>
      </div>
    </div>
  );
}

function ReportingPeriodRows({ projectId, projectReportingPeriods, userPermission }) {
  if (!projectReportingPeriods) return null;

  return projectReportingPeriods
    .toList()
    .sortBy((i) => i.get("startDate"))
    .reverse()
    .map((reportingPeriod, i) => (
      <ReportingPeriodRow
        key={i}
        projectId={projectId}
        userPermission={userPermission}
        reportingPeriod={reportingPeriod}
      />
    ));
}

const formatDateForInput = (isoDateString) => {
  if (!isoDateString) return "";
  return Moment.utc(isoDateString).format("YYYY-MM-DD");
};

function EditReportingDurationModal({ projectId, trigger }) {
  const { data: project } = useApi("/api/project/{projectId}/", { projectId });
  const { hideModal } = useModal();

  if (!project) return null;

  return (
    <Modal title="Edit Reporting Duration">
      <form onSubmit={(e) => editReportingDuration(e, { projectId, trigger, hideModal })}>
        <div className="row">
          <div className="col">
            <div className="form-group">
              <label htmlFor="reporting_start">Reporting from:</label>
              <input
                type="date"
                className="form-control"
                id="reporting_start"
                name="startDate"
                defaultValue={formatDateForInput(project?.getIn?.(["reporting", "startDate"]))}
              />
            </div>
          </div>
          <div className="col">
            <div className="form-group">
              <label htmlFor="reporting_end">Reporting to:</label>
              <input
                type="date"
                className="form-control"
                id="reporting_end"
                name="endDate"
                defaultValue={formatDateForInput(project?.getIn?.(["reporting", "endDate"]))}
              />
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col">
            <div className="form-group">
              <label htmlFor="reporting_freq">Every</label>
              <input
                type="number"
                className="form-control"
                id="reporting_freq"
                name="freq_num"
                defaultValue={project?.getIn?.(["reporting", "freq_num"]) || 1}
              />
            </div>
          </div>
          <div className="col">
            <div className="form-group">
              <label htmlFor="reporting_units">Reporting Increments</label>
              <select
                className="form-control"
                id="reporting_units"
                name="freq_unit"
                defaultValue={project?.getIn?.(["reporting", "freq_unit"]) || "MONTHS"}
              >
                {repPeriodChoices.map((name, i) => (
                  <option key={i} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <button type="submit" className="btn btn-primary">
          Update
        </button>
      </form>
    </Modal>
  );
}

function CreateFromCSVScheduleModal({ projectId, trigger }) {
  const { hideModal } = useModal();

  return (
    <Modal title="Create From CSV Schedule">
      <form onSubmit={(e) => createFromSchedule(e, { projectId, trigger, hideModal })}>
        <div className="form-group">
          <label htmlFor="schedule">
            Comma-separated start-dates of each period in format (dd/mm/yy, dd/mm/yy, etc)
          </label>
          <input type="text" className="form-control" id="schedule" name="schedule" required />
        </div>
        <button type="submit" className="btn btn-primary">
          Create
        </button>
      </form>
    </Modal>
  );
}

export default function ReportingPeriodPanel() {
  const { projectId } = useFullIds();
  const { trigger: triggerProject } = useApi("/api/project/{projectId}/", { projectId });
  const { data: projectReportingPeriods, trigger: triggerReportingPeriods } = useApi(
    "/api/project/{projectId}/reporting_periods/",
    { projectId }
  );
  const { setModal } = useModal();

  const trigger = () => {
    console.log("Triggering refresh of project and reporting periods");
    triggerProject();
    triggerReportingPeriods();
  };

  console.log("ReportingPeriodPanel render", projectId, projectReportingPeriods?.toJS());

  return (
    <BSCard
      widthOffset="col-sm-12"
      title="Reporting Periods"
      button={
        <BSBtnDropdown
          header={<BSBtn text="Admin" onClick={(e) => {}} />}
          links={[
            {
              name: "Edit Reporting Duration",
              onClick: () => setModal(<EditReportingDurationModal projectId={projectId} trigger={trigger} />),
            },
            {
              name: "Create from schedule",
              onClick: () => setModal(<CreateFromCSVScheduleModal projectId={projectId} trigger={trigger} />),
            },
            { name: "Delete empty periods from end", onClick: (e) => clearEmpty(e, { projectId, trigger }) },
          ]}
        />
      }
      body={
        <>
          <RowHeaders />
          <ReportingPeriodRows projectId={projectId} projectReportingPeriods={projectReportingPeriods} />
        </>
      }
    />
  );
}
